import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/app/api';
import { useEnvironment } from '@/app/environment';
import { PERSONA_LABEL, actingAsDisplay, useSession } from '@/app/session';
import type { Doctor } from '@/app/types';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup,
  DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { SEP } from './FooterLine';
import { signOut } from './SignOutForm';

/** The demo doctors a presenter can act as; the registry list, empty until seeded. */
export function useDemoDoctors(enabled: boolean) {
  return useQuery({ queryKey: ['registry', 'doctors'], queryFn: () => api<Doctor[]>('/api/registry/doctors'), enabled, staleTime: 300_000 });
}

export function EnvironmentLines({ className }: { className?: string }) {
  const env = useEnvironment();
  if (!env.data) return <Skeleton className="h-4 w-48" />;
  const lines: Array<[string, string]> = [
    ['Region', env.data.region],
    ['Bedrock', env.data.bedrockProfile],
    ['Build', env.data.build],
    ['Environment', env.data.env],
    ['LOI', env.data.loiMode],
    ['Dictation', env.data.dictationMode],
  ];
  return (
    <dl className={className}>
      {lines.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 text-xs">
          <dt className="text-beam-text-muted">{k}</dt>
          <dd className="tabular text-right">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function UserMenu() {
  const { me, status, setActingAs, requestedActingAs } = useSession();
  const [open, setOpen] = useState(false);
  const canAct = Boolean(me?.presenter && me?.demoActingAs);
  // The registry list is fetched when the menu opens, not on every page load.
  const doctors = useDemoDoctors(canAct && open);
  if (status === 'loading' || !me) {
    return (
      <div className="flex flex-col items-end gap-1" aria-hidden="true">
        <Skeleton className="h-3 w-24 bg-white/15" />
        <Skeleton className="h-2.5 w-12 bg-white/15" />
      </div>
    );
  }
  const acting = actingAsDisplay(me.actingAsName, me.actingAs);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger className="flex h-9 items-center gap-1.5 rounded-md px-2 text-left text-beam-cream hover:bg-white/10 data-[state=open]:bg-white/10" aria-label="Account menu">
        <span className="flex flex-col leading-tight">
          <span className="text-[13px] font-medium">{me.name}</span>
          <span className="text-[11px] text-beam-on-ink-muted">{acting ? `Acting as ${acting}` : PERSONA_LABEL[me.persona]}</span>
        </span>
        <ChevronDownIcon aria-hidden="true" className="size-4 text-beam-on-ink-muted" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-xs font-normal text-beam-text-muted">Signed in as</span>
          <span className="font-medium">{me.name}</span>
          <span className="text-xs font-normal text-beam-text-muted">{me.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal">
          Persona{SEP}<span className="font-medium text-foreground">{PERSONA_LABEL[me.persona]}</span>
          {acting ? <span className="text-beam-warning"> (acting as {acting} in this tab)</span> : null}
        </DropdownMenuLabel>
        {canAct ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="eyebrow text-beam-text-muted">Act as (this tab)</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={requestedActingAs ?? ''} onValueChange={(v) => setActingAs(v === '' ? null : v)}>
                <DropdownMenuRadioItem value="">Yourself</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="qa">QA</DropdownMenuRadioItem>
                {(doctors.data ?? []).map((d) => (
                  <DropdownMenuRadioItem key={d.slug} value={`doctor:${d.slug}`}>{d.name}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel className="eyebrow text-beam-text-muted">Environment</DropdownMenuLabel>
          <div className="px-2 pb-1.5">
            <EnvironmentLines className="flex flex-col gap-1" />
          </div>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
